import { Controller } from "@hotwired/stimulus"

/**
 * iot--fill-height — grows the report chart by exactly the vertical space
 * freed above it, so folding the collapsible sections (comparison stats,
 * Location B panel) hands their room to the chart instead of leaving a gap.
 *
 * Model: the baseline records the element's document-absolute top in the
 * FULLY-EXPANDED layout plus its rendered height (the h-72 class). When a
 * collapsible above folds, the element's top moves up; the delta IS the
 * freed space, and the height becomes baseline + delta (never below the
 * baseline, so expanding everything back returns the chart to its original
 * size).
 *
 * Trigger: the conek-ui accordion dispatches bubbling `accordion:open` /
 * `accordion:close` CustomEvents on every toggle — exactly the relayouts
 * this controller cares about. NOT a ResizeObserver on document.body: the
 * hotwire layout's body is `h-screen` (a fixed 100vh box that overflows
 * into a viewport scrollbar), so its own box never changes when content
 * folds and the observer would simply never fire. The accordion animates
 * max-height for ~200ms, so each toggle measures twice — immediately, and
 * again once the transition has settled, so the final geometry wins.
 */
export default class extends Controller {
  connect() {
    this.recalc = this.recalc.bind(this)
    this.onToggle = this.onToggle.bind(this)
    this.onResize = this.onResize.bind(this)
    this.baselineTop = null
    this.rebaseline()
    document.addEventListener("accordion:open", this.onToggle)
    document.addEventListener("accordion:close", this.onToggle)
    window.addEventListener("resize", this.onResize)
  }

  disconnect() {
    document.removeEventListener("accordion:open", this.onToggle)
    document.removeEventListener("accordion:close", this.onToggle)
    window.removeEventListener("resize", this.onResize)
    clearTimeout(this.settleTimer)
    cancelAnimationFrame(this.toggleFrame)
    cancelAnimationFrame(this.resizeFrame)
    // Defensive teardown only — leave the element on its h-72 class. The
    // Turbo-restore path is cleaned by rebaseline()'s own height clear on
    // the next connect(), not by this line.
    this.element.style.height = ""
  }

  onToggle() {
    cancelAnimationFrame(this.toggleFrame)
    this.toggleFrame = requestAnimationFrame(this.recalc)
    clearTimeout(this.settleTimer)
    this.settleTimer = setTimeout(this.recalc, 250)
  }

  onResize() {
    // Coalesce the resize-event storm into one layout pass per frame —
    // rebaseline writes styles and forces two synchronous rect reads.
    cancelAnimationFrame(this.resizeFrame)
    this.resizeFrame = requestAnimationFrame(() => this.rebaseline())
  }

  rebaseline() {
    // Carry the space already freed by collapsed sections across the
    // re-measure: the baseline must describe the FULLY-EXPANDED layout.
    // Recording the current (possibly collapsed) top instead would make
    // every later fold compute freed = 0 — one ordinary window resize
    // while a section is folded would permanently disable the growth.
    // Clearing our own height doesn't move our own top, so the carried
    // amount can be measured first. This clear is ALSO what discards a
    // stale inline height on a Turbo-restored page (connect() lands here)
    // — do not remove it in favour of the disconnect() one.
    const freed =
      this.baselineTop == null
        ? 0
        : Math.max(0, this.baselineTop - this.absoluteTop())
    this.element.style.height = ""
    this.baselineTop = this.absoluteTop() + freed
    this.baselineHeight = this.element.getBoundingClientRect().height
    this.recalc()
  }

  recalc() {
    const freed = this.baselineTop - this.absoluteTop()
    const target = this.baselineHeight + Math.max(0, freed)
    if (this.element.style.height !== `${target}px`) {
      this.element.style.height = `${target}px`
    }
  }

  absoluteTop() {
    return (
      this.element.getBoundingClientRect().top +
      (window.scrollY || document.documentElement.scrollTop || 0)
    )
  }
}
